/**
 * Platforms API — fetch available signup platforms.
 * GET /bot/api/platforms → list active platforms (public, no auth)
 */

import { getBaseUrl, buildUrl, parseJson } from './utils.js';

let cachedData = null;
let cachedPromise = null;

function fetchPlatforms() {
  const url = buildUrl(getBaseUrl(), 'bot/api/platforms');
  return fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  }).then(parseJson);
}

/**
 * Fetch the list of active signup platforms.
 * Cached at module scope so the platform selection page can render instantly
 * after signup (the request is warmed during the signup form).
 * @returns {Promise<Array>} array of platform objects
 */
export async function getSignupPlatforms() {
  if (cachedData) return cachedData;
  if (!cachedPromise) {
    cachedPromise = fetchPlatforms()
      .then((data) => { cachedData = data; return data; })
      .catch((err) => {
        // Don't poison the cache on failure — let the next caller retry.
        cachedPromise = null;
        throw err;
      });
  }
  return cachedPromise;
}

/**
 * Synchronous getter — returns cached platforms if already resolved, else null.
 * Lets callers skip the loading state entirely on a warm cache.
 */
export function getCachedSignupPlatforms() {
  return cachedData;
}

/**
 * Fire-and-forget prefetch — call early (e.g. on the signup page) to warm the
 * cache so the platform selection page has data ready on mount.
 */
export function prefetchSignupPlatforms() {
  getSignupPlatforms().catch(() => { /* swallow — real call will retry */ });
}
